<template>
  <div class="live-card">
    <a class="live-cover" :href="liveLink" target="_blank">
      <img class="live-cover-img" :src="cardData?.room_info.cover+'@363w_204h_1e_1c_1f.webp'" @error="onCoverImgError"/>
      <div class="live-cover-bottom">
        <div class="live-icon"></div>
      </div>
    </a>
    <div class="live-info">
      <a class="avatar" 
        @click.stop="gotoSpace(cardData.user_info.mid)" 
        :href="'/space/'+ cardData.user_info.mid + '?fromSpmId=' + spmId" 
        target="_blank"
      >
        <img class="avatar-img" :src="(cardData.user_info.face || DEFAULT_FACE) + '@36w_36h_1e_1c_1f.webp' " @error="onFaceImgError">
      </a>
      <div class="info-content">
        <a class="info-title" :href="liveLink" target="_blank">{{cardData?.room_info.title}}</a>
        <div class="info-desc">
          <div class="info-desc-attention" v-if="cardData.user_info.attention"></div>
          <a class="info-desc-nickname" 
            @click.stop="gotoSpace(cardData.user_info.mid)" 
            :href="'../space/'+ cardData.user_info.mid + '?fromSpmId=' + spmId" 
            target="_blank"
          >
            {{cardData.user_info.name}}
          </a>
          <div class="info-desc-identify"
            v-if="cardData.user_info.identify?.role"
            :style="{backgroundImage: `url(${cardData?.user_info?.identify?.icon})`}"/>
          <span class="info-desc-dot">·</span>
          <a :href="liveLink" target="_blank" class="info-desc-popular">{{formatOnline(cardData?.room_info.online)}}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, useSSRContext, computed } from 'vue';
import { LogReporter } from '@/common/log-report';
import type{ILiveCard} from '@/service/home/types';
import {DEFAULT_FACE} from '@/constants';
import defaultCover from '@/assets/default-cover.png';

const props = defineProps<{cardData: ILiveCard}>();
const {cardData} = toRefs(props);

const spmId = LogReporter.getSpmId();

const formatOnline = (followers:number) => {
  if (followers < 1e3) {
    return followers;
  } else if(followers < 1e6){
    return (followers / 1e3).toFixed(1) + 'K';
  }else if (followers < 1e9) {
    return (followers / 1e6).toFixed(1) + 'M';
  } else {
    return (followers / 1e9).toFixed(1) + 'B';
  }
};

const gotoSpace = (mid:number) => {
  // window.open('../space/'+ mid);
};
 
const onCoverImgError = async(e) => {
  const src = e.target.getAttribute('src');
  if(!src.includes('default-cover')){
    e.target.setAttribute('src', defaultCover);
  }
};

const onFaceImgError = (e) => {
  const src = e.target.getAttribute('src');
  if(!src.includes('noface_v1')){
    e.target.setAttribute('src', DEFAULT_FACE);
  }
};

const liveLink = computed(() => {
  let path;
  if(import.meta.env.SSR){
    const ssrContext = useSSRContext();
    path =  ssrContext?.url;
  }else{
    path = window.location.href;
  }
  return `${path}${path.endsWith('/') ? '' : '/'}${cardData.value?.room_info.room_id}?fromSpmId=${spmId}`;
});

</script>

<style lang="less" scoped>
@import '@/styles/var.less';
@import '@/styles/util.less';

.live-card {
  position: relative;
  // .live-link {
  //   position: absolute;
  //   z-index: 1;
  //   width: 100%;
  //   height: 100%;
  //   top: 0;
  //   left: 0;
  // }

  .live-cover {
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    background-color: #eee;
    display: block;

    .live-cover-img {
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;

      &[src*='default-cover'] {
        width: 48px;
        height: 48px;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .live-cover-bottom {
      position: absolute;
      z-index: 1;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 40px;
      background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 57%, rgba(0, 0, 0, 0.5) 99%);
      border-radius: 0 0 8px 8px;

      .live-icon {
        position: absolute;
        right: 12px;
        bottom: 10px;
        width: 42px;
        height: 16px;
        background: url('@/assets/live_badge.gif') no-repeat center center;
        background-size: 100%;
        border-radius: 2px;
      }
    }
  }

  .live-info {
    margin-top: 8px;
    display: grid;
    grid-template-columns: 36px 1fr;
    column-gap: 8px;

    .avatar {
      width: 36px;
      height: 36px;

      .avatar-img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
      }
    }

    .info-content {
      display: grid;
      grid-template-columns: 1fr;
      row-gap: 4px;

      .info-title {
        font-size: 16px;
        color: #333;
        letter-spacing: 0;
        line-height: 20px;
        font-weight: 700;
        display: block;
        .multi-ellipsis(2);
      }

      .info-desc {
        display: grid;
        align-items: center;
        column-gap: 2px;
        grid-template-columns:
          minmax(auto, max-content)
          minmax(auto, max-content)
          minmax(auto, max-content)
          minmax(auto, max-content)
          auto;

        .info-desc-attention {
          width: 53px;
          height: 16px;
          background: url('@/assets/following.png') no-repeat center center;
          background-size: 100%;
          margin-right: 5px;
        }

        .info-desc-nickname {
          font-size: 14px;
          color: #666;
          letter-spacing: 0;
          line-height: 18px;
          font-weight: 400;
          .text-ellipsis;
        }

        .info-desc-identify {
          width: 16px;
          height: 16px;
          background-repeat: no-repeat;
          background-position: center;
          background-size: 100%;
        }

        .info-desc-dot {
          font-size: 14px;
          color: #666;
          line-height: 18px;
          font-weight: 400;
        }

        .info-desc-popular {
          font-size: 14px;
          color: #666;
          line-height: 18px;
          font-weight: 400;
          padding-left: 16px;
          background: url('@/assets/popularity-icon.png') no-repeat left center;
          background-size: 14px;
        }
      }
    }
  }
}

img:not([src]),
img[src=''] {
  opacity: 0;
}
</style>